import React from "react";
import {cn} from "@/lib/utils";
import {getOptimizedSeavienImageUrl, MEDIUM_IMAGE_SIZE, type ImageSizeLike} from "@/main/util/ImageUrlHelper";

type ProfileBannerProps = {
    imageUrl?: string | null;
    seavienImageSize?: ImageSizeLike | null;
    className?: string;
    contentClassName?: string;
    children?: React.ReactNode;
    /**
     * A stable hook for the band itself. `EMPTY-STATES-98` needs a test to distinguish "the band is
     * carrying a picture" from "the band collapsed because there is nothing to carry", and that is a
     * statement about the element, not about a class string — asserting the Tailwind literal would pin
     * the measurement rather than the decision.
     */
    dataPlaywrightId?: string;
};

export function ProfileBanner({
    imageUrl,
    seavienImageSize = MEDIUM_IMAGE_SIZE,
    className,
    contentClassName,
    children,
    dataPlaywrightId
}: ProfileBannerProps) {
    /**
     * ⚠️ THE BAND IS THEME-RELATIVE, so no child of it may hardcode a colour.
     *
     * <p>Both branches below are `color-mix` over `--background2` / `--background1` /
     * `--primary-action`, every one of which is redefined in `.dark`. In LIGHT the band therefore
     * resolves to a near-white ground, and a child painting `text-white` on it measured **1.24:1** on
     * the heading and 1.22:1 on the body (`DESIGN-SYSTEM-1`, on `/component`'s own demo overlay — the
     * design language's reference surface). Content overlaid here reads the shadcn semantic tokens
     * (`text-foreground`, `text-muted-foreground`, `border-border`, a `bg-card/NN` panel) so it tracks
     * whichever end of the ramp the band actually resolved to.
     *
     * <p>An image behind the band does NOT license white either: `contentClassName` positions the
     * overlay anywhere in the band, the picture is member-supplied, and the gradient above is a tint
     * rather than a scrim. Guarded for this file's one in-repo consumer pair by
     * `main/react/page/ComponentPage.designLanguage.spec.ts`.
     */
    const resolvedImageUrl = getOptimizedSeavienImageUrl(imageUrl, seavienImageSize);
    const bannerBackground = resolvedImageUrl
        ? `linear-gradient(135deg, color-mix(in srgb, var(--background2) 72%, transparent), color-mix(in srgb, var(--primary-action) 14%, transparent), color-mix(in srgb, var(--background3) 68%, transparent)), url(${resolvedImageUrl})`
        : "linear-gradient(135deg, color-mix(in srgb, var(--background2) 94%, var(--primary-action)), color-mix(in srgb, var(--background1) 97%, var(--primary-action)), color-mix(in srgb, var(--background3) 98%, var(--primary-action)))";

    return (
        <div
            className={cn("relative rounded-t-3xl overflow-hidden", className)}
            data-playwright-id={dataPlaywrightId}
            style={{
                backgroundImage: bannerBackground,
                backgroundSize: resolvedImageUrl ? "cover" : undefined,
                backgroundPosition: resolvedImageUrl ? "center" : undefined
            }}
        >
            <div
                className="absolute inset-0"
                style={{
                    background: "radial-gradient(circle at top right, color-mix(in srgb, var(--background1) 16%, transparent), transparent 36%), radial-gradient(circle at bottom left, color-mix(in srgb, var(--primary-action) 10%, transparent), transparent 32%)"
                }}
            />
            <div
                className="absolute left-6 top-6 size-24 rounded-full blur-3xl"
                style={{
                    background: "color-mix(in srgb, var(--background1) 10%, transparent)"
                }}
            />
            <div
                className="absolute bottom-0 right-0 size-36 translate-x-8 translate-y-8 rounded-full blur-3xl"
                style={{
                    background: "color-mix(in srgb, var(--primary-action) 10%, transparent)"
                }}
            />

            {/* A98 (DESIGN-SYSTEM-15, 2026-09-28): IN FLOW below `sm`, overlaid from `sm` up. The overlay used
                to be `absolute inset-0` at every width, so it added nothing to the band's height: at phone width
                a card taller than the band grew upward past its top edge and `overflow-hidden` cut off its top.
                In flow, the band grows with its content. `min-h-[inherit]` keeps the wrapper at least the
                band's own `min-h-*` (every children-bearing consumer sizes the band that way), so a consumer
                that pins a pill with `absolute bottom-0` inside it still lands on the band's bottom edge. */}
            {children ? <div className={cn("relative z-10 min-h-[inherit] sm:absolute sm:inset-0", contentClassName)}>{children}</div> : null}
        </div>
    );
}
